/**
 * @fileoverview reCAPTCHA v3 Provider using Next.js Script with Lazy Loading
 *
 * SSR Exception: MUST use 'use client'
 * Reason: Uses Next.js Script component with client-side script loading and React Context.
 *
 * Performance Strategy:
 * - Uses `lazyOnload` strategy to load script during browser idle time
 * - Submit button disabled until reCAPTCHA is ready
 * - Optimizes Core Web Vitals (LCP, INP) by deferring non-critical script
 *
 * Reference: https://developers.google.com/recaptcha/docs/v3
 */
"use client";

import Script from "next/script";
import type React from "react";
import { createContext, useCallback, useContext, useState } from "react";

interface RecaptchaContextValue {
	isReady: boolean;
	siteKey: string | null;
}

const RecaptchaContext = createContext<RecaptchaContextValue>({
	isReady: false,
	siteKey: null,
});

export const useRecaptcha = () => useContext(RecaptchaContext);

interface RecaptchaProviderProps {
	children: React.ReactNode;
}

/**
 * reCAPTCHA provider that lazy-loads the Google reCAPTCHA v3 script.
 *
 * The script loads using `lazyOnload` strategy, which defers loading until
 * browser idle time. This prevents the script from competing with critical
 * resources during page load.
 *
 * Consumer components should:
 * - Read `isReady` from `useRecaptcha()` hook
 * - Disable submit button until `isReady === true`
 * - Use `window.grecaptcha.ready()` before calling `execute()`
 */
export function RecaptchaProvider({ children }: RecaptchaProviderProps) {
	const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || null;
	const [isReady, setIsReady] = useState(false);

	const handleScriptLoad = useCallback(() => {
		if (window.grecaptcha) {
			window.grecaptcha.ready(() => setIsReady(true));
		}
	}, []);

	if (!siteKey) {
		return (
			<RecaptchaContext.Provider value={{ isReady: false, siteKey: null }}>
				{children}
			</RecaptchaContext.Provider>
		);
	}

	return (
		<RecaptchaContext.Provider value={{ isReady, siteKey }}>
			<Script
				src={`https://www.google.com/recaptcha/api.js?render=${siteKey}`}
				strategy="lazyOnload"
				onLoad={handleScriptLoad}
				id="recaptcha-script"
			/>
			{children}
		</RecaptchaContext.Provider>
	);
}
